import React from 'react';
import Modal from '../../modal/index';
import EmailVerificationModal from '../email-verification-modal/index';
import './chat-init-modal.css';
import { t, getCurrentLanguage } from '../../../utils/translations';
import useLanguageStore from '../../../store/languageStore';
import { GATE_BASE_URL, gateFetchOptions } from '../gate-api';

class ChatInitModal2610 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      recaptchaToken: null,
      isLoading: false,
      error: null,
      emailError: null,
      currentLanguage: getCurrentLanguage(),
      showVerificationModal: false,
      verificationSent: false,
      // Local development only: see the comment where this is set.
      devVerificationCode: null
    };
    this.recaptchaRef = React.createRef();
    this.recaptchaWidgetId = null;
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.loadRecaptchaScript();
    }
    
    // Subscribe to language changes
    this.unsubscribe = useLanguageStore.subscribe((state) => {
      this.setState({ currentLanguage: state.currentLanguage });
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.loadRecaptchaScript();
      // Reset form when modal opens
      this.setState({
        email: '',
        recaptchaToken: null,
        error: null,
        emailError: null,
        showVerificationModal: false,
        verificationSent: false
      });
    }
  }

  componentWillUnmount() {
    // Clean up reCaptcha
    if (this.recaptchaWidgetId !== null && window.grecaptcha) {
      try {
        window.grecaptcha.reset(this.recaptchaWidgetId);
      } catch (error) {
        console.error('reCaptcha cleanup error:', error);
      }
    }
    
    // Clean up language subscription
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  loadRecaptchaScript = () => {
    // Check if reCaptcha script already exists
    if (window.grecaptcha || document.querySelector('script[src*="recaptcha"]')) {
      this.initializeRecaptcha();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.initializeRecaptcha();
    };
    script.onerror = () => {
      console.error('❌ Failed to load reCaptcha script');
      this.setState({ error: t('recaptcha_load_error', this.state.currentLanguage) });
    };

    document.head.appendChild(script);
  };

  initializeRecaptcha = () => {
    // Wait for grecaptcha to be available
    const checkRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.render) {
        try {
          // Clear any existing widget
          if (this.recaptchaRef.current) {
            this.recaptchaRef.current.innerHTML = '';
          }

          this.recaptchaWidgetId = window.grecaptcha.render(this.recaptchaRef.current, {
            sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcEOtorAAAAAJ1b6PwUzIKcokQBiLxy5ULpLHX9', // Production site key
            callback: this.handleRecaptchaCallback,
            'expired-callback': this.handleRecaptchaExpired,
            'error-callback': this.handleRecaptchaError,
            theme: 'light',
            size: 'normal'
          });
        } catch (error) {
          console.error('❌ Failed to initialize reCaptcha:', error);
          this.setState({ error: t('recaptcha_init_error', this.state.currentLanguage) });
        }
      } else {
        setTimeout(checkRecaptcha, 100);
      }
    };

    checkRecaptcha();
  };

  handleRecaptchaCallback = (token) => {
    this.setState({ 
      recaptchaToken: token,
      error: null 
    });
  };

  handleRecaptchaExpired = () => {
    this.setState({ 
      recaptchaToken: null,
      error: t('recaptcha_expired_error', this.state.currentLanguage) 
    });
  };

  handleRecaptchaError = () => {
    console.error('❌ reCaptcha error occurred');
    this.setState({ 
      recaptchaToken: null,
      error: t('recaptcha_error', this.state.currentLanguage) 
    });
  };

  handleEmailChange = (event) => {
    const email = event.target.value;
    this.setState({ 
      email,
      emailError: null // Clear email error when user types
    });
  };

  validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    
    const { email, recaptchaToken } = this.state;
    
    // Validate email
    if (!email.trim()) {
      this.setState({ emailError: t('email_required_error', this.state.currentLanguage) });
      return;
    }
    
    if (!this.validateEmail(email)) {
      this.setState({ emailError: t('email_invalid_error', this.state.currentLanguage) });
      return;
    }
    
    // Validate reCaptcha
    if (!recaptchaToken) {
      this.setState({ error: t('recaptcha_required_error', this.state.currentLanguage) });
      return;
    }
    
    this.setState({ isLoading: true, error: null, emailError: null });
    
    try {
      // Step 1: send the verification email.
      //
      // Same-origin, unlike 2510. The auto-registration branch of this endpoint
      // completes the gate outright and sets the visitor cookie, and a Lax cookie
      // on a cross-site response is discarded by the browser. See gate-api.js.
      const response = await fetch(`${GATE_BASE_URL}/send-verification`, {
        method: 'POST',
        ...gateFetchOptions,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email.trim(),
          language: this.state.currentLanguage || 'pt'
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Track verification email sent
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'verification_email_sent',
            event_category: 'User Registration',
            event_action: 'Verification Email Sent',
            event_label: data.auto_registered ? 'Auto Registered' : 'Token Sent',
            current_language: window.current_language || 'pt',
            user_email_domain: email.split('@')[1],
            auto_registered: data.auto_registered || false
          });
        }
        
        if (data.auto_registered) {
          // User was auto-registered (verified within 24h)
          await this.handleVerificationSuccess({ 
            email, 
            registrationData: data,
            autoRegistered: true 
          });
        } else {
          // Show verification modal for token input.
          //
          // `dev_verification_code` is present only when the backend is running
          // locally with EXPOSE_VERIFICATION_CODE on, which it refuses to do
          // outside development. In every real environment the field is absent and
          // this is undefined, so the field starts empty as always.
          this.setState({
            isLoading: false,
            verificationSent: true,
            showVerificationModal: true,
            devVerificationCode: data.dev_verification_code || null
          });
          
          // Close the current modal
          this.props.onClose();
        }
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        
        // Track verification email failure
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'verification_email_failed',
            event_category: 'User Registration',
            event_action: 'Verification Email Failed',
            event_label: errorData.detail || 'Send Verification Error',
            current_language: window.current_language || 'pt'
          });
        }
        
        this.setState({ 
          error: errorData.detail || t('verification_send_error', this.state.currentLanguage),
          isLoading: false 
        });
      }
      
    } catch (error) {
      console.error('❌ Error sending verification email:', error);
      this.setState({ 
        error: t('verification_network_error', this.state.currentLanguage),
        isLoading: false 
      });
      
      // Reset reCaptcha on error
      if (this.recaptchaWidgetId !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(this.recaptchaWidgetId);
          this.setState({ recaptchaToken: null });
        } catch (resetError) {
          console.error('Error resetting reCaptcha:', resetError);
        }
      }
    }
  };

  handleVerificationSuccess = async ({ email, registrationData, autoRegistered = false }) => {
    try {
      // Track successful registration
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'user_registered',
          event_category: 'User Registration',
          event_action: 'Registration Completed',
          event_label: autoRegistered ? 'Auto Registration' : 'Email Verification',
          current_language: window.current_language || 'pt',
          user_email_domain: email.split('@')[1],
          registration_method: autoRegistered ? 'auto' : 'verification'
        });
      }
      
      // Call the parent component's callback to initialize the chat
      if (this.props.onInitializeChat) {
        await this.props.onInitializeChat({ 
          email, 
          recaptchaToken: this.state.recaptchaToken,
          registrationData 
        });
      }
      
      // Close all modals
      this.setState({ showVerificationModal: false });
      this.props.onClose();
      
    } catch (error) {
      console.error('❌ Error in chat initialization after verification:', error);
      this.setState({ 
        error: t('chat_init_error', this.state.currentLanguage),
        showVerificationModal: false 
      });
    }
  };

  handleVerificationModalClose = () => {
    this.setState({ showVerificationModal: false });
  };

  handleClose = () => {
    // Reset form state when closing
    this.setState({
      email: '',
      recaptchaToken: null,
      isLoading: false,
      error: null,
      emailError: null,
      showVerificationModal: false,
      verificationSent: false
    });
    
    // Reset reCaptcha
    if (this.recaptchaWidgetId !== null && window.grecaptcha) {
      try {
        window.grecaptcha.reset(this.recaptchaWidgetId);
      } catch (error) {
        console.error('reCaptcha reset error:', error);
      }
    }
    
    this.props.onClose();
  };

  render() {
    const { isOpen } = this.props;
    const { email, isLoading, error, emailError, recaptchaToken, currentLanguage, showVerificationModal } = this.state;

    return (
      <>
        <Modal isOpen={isOpen} onClose={this.handleClose}>
          <div className="chat-init-modal">
            <div className="chat-init-header">
              <h2>{t('chat_init_title', currentLanguage)}</h2>
              <p>{t('chat_init_description', currentLanguage)}</p>
            </div>

            <form onSubmit={this.handleSubmit} className="chat-init-form">
              {/* Email Input */}
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  {t('email_label', currentLanguage)} <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={this.handleEmailChange}
                  placeholder={t('email_placeholder', currentLanguage)}
                  className={`form-input ${emailError ? 'error' : ''}`}
                  disabled={isLoading}
                  autoComplete="email"
                  required
                />
                {emailError && (
                  <span className="error-message">{emailError}</span>
                )}
              </div>

              {/* reCaptcha */}
              <div className="form-group">
                <label className="form-label">
                  {t('security_verification_label', currentLanguage)} <span className="required">*</span>
                </label>
                <div className="recaptcha-container">
                  <div ref={this.recaptchaRef}></div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="error-message-box">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <div className="form-actions">
                <button
                  type="submit"
                  className="chat-init-button"
                  disabled={isLoading || !email.trim() || !recaptchaToken}
                >
                  {isLoading ? (
                    <>
                      <span className="loading-spinner"></span>
                      {t('sending_verification_button', currentLanguage)}
                    </>
                  ) : (
                    t('send_verification_button', currentLanguage)
                  )}
                </button>
              </div>
            </form>

            <div className="chat-init-footer">
              <p className="privacy-note">
                <small>
                  {t('privacy_note', currentLanguage)}
                </small>
              </p>
            </div>
          </div>
        </Modal>

        {/* Email Verification Modal */}
        <EmailVerificationModal
          isOpen={showVerificationModal}
          email={email}
          devVerificationCode={this.state.devVerificationCode}
          onClose={this.handleVerificationModalClose}
          onVerificationSuccess={this.handleVerificationSuccess}
        />
      </>
    );
  }
}

export default ChatInitModal2610;
